import { el, clear, toast, fmtRemaining } from '../../dom';
import { handleSessionExpiry } from '../../session-expiry';
import { fieldApi, shopApi, craftingApi, woodcuttingApi, miningApi, CRAFT_MATERIALS, type CraftStatus } from '../../game-api';
import { tierTint, armourTintTier } from '../../map/ores';
import { ApiError } from '../../../core/network/api';
import { dispatchAction, isDropped } from '../../action/dispatch';
import { closePlanter, cropById, guard, syncHeld, setTorchLit, BUILDINGS, openOverlay, liveRemaining, type FarmCtx, setBagPainter, refreshBag, setFarm, setEnergyStatus} from './context';
import { cancelPlacementForHand, exitBuildMode } from './input';
import { openTentMenu, startTentPlacement, syncSleepOverlay } from './building-menu';
import { TENT_SAFE_RADIUS_TILES } from './placement';
import { affordBlocker } from './paint-run';
import { villageApi } from '../../game-api';
import { refreshGear } from './scene';
import { openVillage } from './village';
import { richText, stripEmoji, gicon, type IconName } from '../../ui/icons';
import { toolIconUrl, type ToolId } from '../../tool-config';
import { playSfx } from '../../audio/audio';
import { isDecorKind, isGroundKind } from '../../map/decor-kinds';
import { itemIconSrc, craftSlotIconSrc, CRAFT_SLOT_ICON, armourSlotIconSrc, armourIconSrc, PAGER_ICON } from '../../ui/item-icons';
import { armourIsPainted } from '../../ui/armor-art';
import {
  chromeBottom, modalWrap, qtyBadge,
  ARMOUR_COL, BAG_GRID, SLOT, STAT_BARS, TOOL_ROW,
  bagAttack, bagCellRect, bagDefence, bagPageCount, bagPageSlice, boardPlate, boardRect, boardScale,
  clampBagPage, toolCellRect,
} from '../../ui/panel';
import { isPhoneLayout } from '../../mobile/layout';
import { TOOL_TIER_KEY, toolSlotTitle, toolTierColor, toolTierShort, type TieredTool } from './tool-tier';
import { purchaseQuote } from './hp-bar';

/**
 * The building-count icon: the placed House the counter is actually counting.
 *
 * Deliberately one of the real building thumbnails from `sprites/buildings/`
 * rather than an ICON entry — the icon set has no "building", and the nearest
 * matches (hammer, hoe) are TOOLS, which is exactly how the counter ended up
 * labelled with a tool glyph.
 */
export const BUILDING_COUNT_ICON = '/sprites/buildings/house.png';

/**
 * Did a wrapping flex row fail to fit its content on the available width?
 *
 * Pure so the rule can be asserted without a layout engine. 1px of slack absorbs
 * sub-pixel rounding, which otherwise reports permanent overflow on fractional
 * zoom levels and would collapse a bar that fits.
 */
export function rowOverflows(scrollW: number, clientW: number): boolean {
  return scrollW - clientW > 1;
}

// ─── HUD chrome (DOM) ───────────────────────────────────────────────────────
export function paintChrome(c: FarmCtx) {
  const { farm, energyStatus: en } = c;
  lastCtx = c;
  // The one call site of the sleep screen. Here rather than in the sleep handler
  // because paintChrome runs after every load and every action response, so the
  // overlay also comes back on a page reload mid-sleep — otherwise a refresh left
  // a farm that silently refused every action with nothing on screen to explain it.
  syncSleepOverlay(c);
  if (!farm) return;
  const pct = Math.round((farm.energy / farm.maxEnergy) * 100);
  clear(c.energyBar);

  // Energy no longer trickles back — it is spent from a bar and bought back with
  // rest charges (n/5). The old "+1/min" readout was a lie; show the charges and a
  // Rest button instead, and a lockout countdown when the charges are gone.
  const chargeDots = en
    ? Array.from({ length: en.maxRefills }, (_, i) =>
        el('span', i < en.refills ? 'text-energy' : 'text-text-dim/40', {}, '●')).map((x) => x)
    : [];
  // 16px tall at 10px type was a dot on a phone. The class carries the coarse-
  // pointer growth (app.css) so the painted chip keeps its size on desktop.
  // Live countdown to the next charge. Its text is set every frame; the element is
  // created here so the layout owns it.
  const restTimer = el('span', 'font-mono text-white/55', {},
    fmtNextCharge(en?.refills ?? 0, en?.maxRefills ?? 0, en?.nextRefillMs) ?? '');
  c.restTimerLabel = restTimer;
  const restBtn = el('button',
    'tb-rest-btn text-[11px] px-2 py-0.5 rounded border border-white/30 bg-black/40 text-white/85 hover:border-energy hover:text-energy disabled:opacity-40',
    { onClick: () => void doRest(c) }, 'Rest');
  // Out of charges the Rest button used to grey out and that was the whole answer:
  // the one control on the energy row became dead, and the extension offer only
  // appeared if you kept clicking it. Spent charges now swap it for a live CTA.
  const outOfRest = !!en && (en.refills <= 0 || en.lockedOut);
  const buyEnergyBtn = el('button',
    'tb-rest-btn text-[11px] px-2 py-0.5 rounded border border-shroom bg-black/40 text-shroom hover:bg-shroom/20',
    { onClick: () => openEnergyModal(c) }, 'Buy energy');

  // Box-less, one line: bolt · bar · numbers, then a thin rest row. Matches the HP
  // readout above it (see design/gdd/hud-bars.md) — no card, no border, no blur.
  c.energyBar.append(
    el('div', 'flex items-center gap-1.5 text-[11px]', {},
      ...richText('⚡', 13),
      el('div', 'flex-1 h-2 rounded-full bg-black/55 overflow-hidden border border-black/60', {},
        el('div', 'h-full bg-energy transition-all', { style: `width:${pct}%` })),
      el('span', 'font-mono text-white/90', {}, `${farm.energy}/${farm.maxEnergy}`)),
    el('div', 'flex items-center gap-1.5 mt-0.5 text-[11px] text-white/70', {},
      ...chargeDots,
      // The countdown sits with the circles it belongs to. Ticked from the frame
      // loop (see farm.ts, next to the clock) rather than repainted here: a full
      // paintChrome every second would rebuild the footer buttons under the
      // player's finger.
      restTimer,
      ...(en && en.lockedOut ? [el('span', 'text-error font-mono', {}, fmtLock(en.lockedOutMs))] : []),
      outOfRest ? buyEnergyBtn : restBtn));
  // Game-time clock, refreshed every frame from the day/night config (see farm tick).
  const clock = el('div', 'game-font mt-0.5 text-[11px] text-white/70', {}, '—');
  c.clockLabel = clock;
  c.energyBar.append(clock);
  clear(c.footer);
  // Block 8: Build toggles placement mode; the label reflects on/off + the cap.
  // `shrink-0 whitespace-nowrap` on every chip: the footer is a WRAPPING flex row
  // with `max-w-[58vw]`, and a flex item whose text is allowed to break still
  // reports a min-content width — the label "Village" broke mid-word and the button
  // box kept its content width, so both buttons hung out past the panel's border.
  // Nowrap makes each chip an indivisible box, and the wrap then does its job.
  const chip = 'px-2.5 py-1 rounded border text-sm inline-flex items-center gap-1.5 shrink-0 whitespace-nowrap';
  const buildBtn = el('button',
    `${chip} ${c.buildMode ? 'border-gold text-gold' : 'border-border hover:border-gold hover:text-gold'}`,
    { onClick: () => openBuild(c), 'data-tut': 'build' },
    ...richText(c.buildMode ? '🔨 Building…' : '🏗️ Build'));
  const villageBtn = () => el('button', `${chip} border-border hover:border-gold hover:text-gold`, { onClick: () => openVillage(c) },
    el('img', 'w-4 h-4 object-contain', { src: '/sprites/npc/forager.png', style: 'image-rendering:pixelated' }), 'Village');
  // Only the STRUCTURE cap is shown, and it says what it counts.
  //
  // The plot counter went with the plot limit: hoeing is capped by energy now, not
  // by a ceiling, so "14/60 plots" was reporting a rule that no longer exists — and
  // the bare "n/m" beside a seedling icon read as a mystery either way. Fences and
  // ground decor have their own generous caps and are deliberately not shown; they
  // are placed by the dozen and a counter for them would be noise.
  //
  // The icon is the game's OWN house sprite, not the 🏗️ glyph: richText maps that
  // emoji to the HAMMER icon, so "14/20 buildings" was labelled with a tool — the
  // same picture the Build button already carries, and nothing about a building.
  const caps = () => el('span', 'ml-1 text-[11px] text-text-dim font-mono self-center inline-flex items-center gap-1 shrink-0 whitespace-nowrap', {},
    el('img', 'inline-block shrink-0', {
      src: BUILDING_COUNT_ICON, alt: '',
      style: 'height:14px;width:auto;image-rendering:pixelated',
    }),
    `${farm.buildingCount}/${farm.maxBuildings} buildings`);

  // A14: no more Craft/Cook buttons — build a Crafting Table / Furnace / Forge
  // (in Build mode) or a Campfire, then walk up and click the station to use it.
  const paintCompactActions = () => {
    // Compact: Build / Village collapse behind one "⋯" button. Labelled buttons
    // plus the cap counters overflow a landscape phone's top-right corner and
    // crowd the HP/energy column; expanded, they drop down as a menu.
    let open = false;
    // Opens UPWARD: on a phone this cluster is the top of the right-hand thumb rail,
    // a finger's length above the bottom edge, so a downward menu would open into
    // the hotbar and off the screen. Above it is empty play area at every size.
    const menu = el('div', 'absolute right-0 bottom-full mb-1 flex flex-col gap-1 p-1.5 rounded-lg bg-surface border border-border shadow-lift z-40 hidden');
    const shut = () => { open = false; menu.classList.add('hidden'); };
    menu.append(buildBtn, villageBtn(), caps());
    for (const child of Array.from(menu.children)) child.addEventListener('click', shut);
    const more = el('button', 'px-2.5 py-1 rounded border border-border text-sm inline-flex items-center gap-1.5 hover:border-gold hover:text-gold',
      { title: 'Build and Village', onClick: (ev: Event) => { ev.stopPropagation(); open = !open; menu.classList.toggle('hidden', !open); } },
      ...richText(c.buildMode ? '🔨' : '🏗️'), '⋯');
    // The tutorial anchors on `[data-tut="build"]`. Inside a COLLAPSED menu that
    // element is display:none, so its rect is all zeroes and the coach ring drew an
    // 8x8 box in the host's top-left corner while the arrow pointed at nothing —
    // exactly the "tap Build" step a new phone player hits first. On a compact HUD
    // the thing they must actually tap is the "⋯", so the anchor moves there and the
    // hidden button gives it up (two matches would resolve to the hidden one).
    buildBtn.removeAttribute('data-tut');
    more.dataset.tut = 'build';
    c.footer.append(el('div', 'relative', {}, more, menu));
  };

  if (isCompactHud()) paintCompactActions();
  else {
    c.footer.append(buildBtn, villageBtn(), caps());
    // The 900px breakpoint is a GUESS about where the labelled bar stops fitting:
    // `max-w-[58vw]` plus a pixel font mean the real limit moves with the font, the
    // counter's digit count and the browser's zoom, which is why the bar still
    // spilled past its frame just above the breakpoint. So the row is MEASURED once
    // it is laid out and collapses on real overflow instead of a predicted width —
    // and the collapsed form is the SAME "⋯" menu, not a second patch. jsdom has no
    // layout engine and reports zeroes, which reads as "fits".
    if (rowOverflows(c.footer.scrollWidth, c.footer.clientWidth)) {
      clear(c.footer);
      paintCompactActions();
    }
  }
  paintHotbar(c);
}


/**
 * A panel docked under the game chrome, instead of a modal over everything.
 *
 * The Build picker used to mount as `fixed inset-0 z-40` with a dark backdrop: it
 * sat ON TOP of the header (z-30), dimmed the map you are about to build on, and
 * had no internal scrolling, so a long list simply ran off the screen. All three
 * are wrong for a tool you use WHILE looking at the world.
 *
 * This docks it to the right, starting below whatever chrome is currently showing
 * (measured, so it follows immersive mode and the header toggle), caps its height
 * to the space left, and scrolls inside. No backdrop — the farm stays visible and
 * clickable, which is the entire point: you pick a piece, then tap a tile.
 */
function dockedSheet(c: FarmCtx, title: string, subtitle: string, body: HTMLElement): HTMLElement {
  // Bottom of the lowest visible chrome bar = where the sheet starts.
  const chrome = chromeBottom();
  // Immersive mode hides the header AND the nav, so `chrome` is 0 and the sheet
  // would start at y=8 — exactly where the immersive floating cluster (chat /
  // settings / "UI", z-56) sits. That cluster drew OVER the sheet's `×`, which was
  // the panel's only exit: every click on the close button hit a button behind it,
  // and there was no Escape and no backdrop. So in immersive mode the sheet starts
  // BELOW the cluster and sits above it in the stack, and openOverlay adds Escape.
  const immersive = document.body.dataset.immersive === 'on';
  const top = (chrome || (immersive ? 44 : 0)) + 8;
  const sheet = el('div',
    'tb-build-sheet fixed right-2 z-[60] w-[320px] max-w-[86vw] flex flex-col rounded-lg bg-surface border border-border shadow-lift sheet-card overscroll-contain',
    { style: `top:${top}px;max-height:calc(100vh - ${top + 12}px)` },
    el('div', 'flex justify-between items-center gap-2 px-3 pt-3 pb-2 shrink-0', {},
      el('h3', 'font-heading text-gold inline-flex items-center gap-1.5', {}, ...richText(title)),
      el('button', 'tb-close-x px-2 py-1 rounded border border-border text-text-dim hover:border-gold hover:text-gold text-sm leading-none',
        { title: 'Close (Esc)', onClick: () => closePlanter(c) }, ...richText('✕ Close'))),
    el('div', 'text-[11px] text-text-dim px-3 pb-2 shrink-0', {}, subtitle),
    el('div', 'flex-1 min-h-0 overflow-y-auto px-3 pb-3', {}, body));
  return sheet;
}

// ─── Build mode (Block 8) ─────────────────────────────────────────────────────
// A small picker of placeable structures; choosing one arms build mode (green/red
// tile frame + tap-to-place), and "Done" leaves it. Costs are paid in gathered
// wood/stone, shown per item.
/**
 * May this kind be armed at all — population, the one-build-at-a-time rule, and
 * whether the player can actually pay for it. Returns the sentence to say, or null.
 *
 * Cost used to be COSMETIC: an unaffordable row was faded and still armed on click,
 * so the player picked a kind, hunted for a green tile and was refused by the server
 * a second later — the exact dead end the two gates below already exist to prevent.
 * The refusal is a toast rather than a `disabled` button, because a row that does
 * nothing when tapped teaches the player the HUD is broken; it has to say why.
 *
 * Cost is checked LAST so the harder walls (villagers, a build already going up)
 * keep their wording, and it goes through paint-run's affordBlocker so the "no bag
 * loaded means never block — the server is the authority" rule is inherited rather
 * than re-stated.
 */
export function armBlocker(
  def: { name: string; wood: number; stone: number; buildMin?: number; needPop?: number } | undefined,
  have: { wood: number; stone: number } | null,
  population: number | null,
  busy: { name: string; leftMs: number } | null,
): string | null {
  if (!def) return null;
  if (def.needPop && population !== null && population < def.needPop) {
    return `${def.name} needs ${def.needPop} villagers — summon them in the Village`;
  }
  // Instant placements (fences, roads, decor) have no build timer and never block,
  // which is why this tests the timer rather than the kind.
  if (busy && def.buildMin) {
    return `${busy.name} is still going up${busy.leftMs ? ` — ${fmtRemaining(busy.leftMs)} left` : ''}. `
      + 'Finish or rush it first.';
  }
  if (have && affordBlocker(def, have, 0)) {
    // Name the SHORTFALL, not the price: the price is already printed on the row, so
    // repeating it answers a question the player did not ask. Words, no emoji — the
    // toast renders as text.
    const short = [
      have.wood < def.wood ? `${def.wood - have.wood} more wood` : '',
      have.stone < def.stone ? `${def.stone - have.stone} more stone` : '',
    ].filter(Boolean).join(' and ');
    return `Not enough materials for ${def.name} — needs ${short}`;
  }
  return null;
}

export function openBuild(c: FarmCtx) {
  // Toggle off if already building (or mid-move).
  if (c.buildMode) { exitBuildMode(c); paintChrome(c); return; }
  // …and toggle the PICKER off if that is what is open. Without this the Build
  // button closed the sheet and immediately re-appended an identical one, so with
  // the `×` unreachable in immersive mode there was no way out at all.
  if (c.overlay?.dataset.panel === 'build') { closePlanter(c); return; }
  closePlanter(c);
  // Population gates the Hamlet buildings (Mine Shaft, Lumber Hut, Forager Hut,
  // Farmhouse). Asked once per open, and applied when it lands: the rows render
  // immediately rather than waiting on a request that may never answer.
  void villageApi.get().then((v) => { c.population = v.count; if (c.overlay?.dataset.panel === 'build') paintBuildLocks(c); }).catch(() => undefined);
  // The scrollable list; the sheet chrome (title, close, hint) wraps it below.
  const card = el('div', 'flex flex-col');
  const arm = (kind: string, name: string) => {
    const busy = (c.farm?.buildings ?? []).find((b) => b.isReady === false);
    const why = armBlocker(BUILDINGS[kind], c.bagCache?.resources ?? null, c.population,
      busy ? { name: BUILDINGS[busy.kind]?.name ?? 'A building', leftMs: liveRemaining(c, busy.remainingMs ?? 0) } : null);
    if (why) { toast(why, 'error'); return; }
    c.buildMode = kind; c.moving = null; closePlanter(c); paintChrome(c);
    toast(`Placing ${name} — tap a green tile`);
  };
  // Hidden entries are the superseded per-shape fence variants (fences auto-connect now).
  const entries = Object.entries(BUILDINGS).filter(([, d]) => !d.hidden);

  const affordable = (def: { wood: number; stone: number }) =>
    !!c.bagCache && c.bagCache.resources.wood >= def.wood && c.bagCache.resources.stone >= def.stone;
  /**
   * A building thumbnail in a FIXED square, contained and centred.
   *
   * The baked building art is bbox-trimmed at pack pixels, so a scaffold is 103px
   * tall and a lantern 16px: sizing the <img> itself let a tall structure tower
   * over the row while a small prop sat as a speck. The box owns the size, the
   * image only has to fit inside it — same footprint for every entry.
   */
  const thumb = (kind: string, box: string) =>
    el('div', `${box} flex items-center justify-center shrink-0`, {},
      el('img', 'max-w-full max-h-full object-contain',
        { src: `/sprites/buildings/${kind}.png`, style: 'image-rendering:pixelated' }));

  /** One full-width row: big thumbnail, name, blurb, both costs. For structures. */
  const bigRow = ([kind, def]: [string, typeof BUILDINGS[string]]) => el('button',
    `w-full flex items-center gap-3 p-2 mb-1.5 rounded border text-left ${affordable(def) ? 'border-border hover:border-gold' : 'border-border opacity-50 cursor-not-allowed'}`,
    { onClick: () => arm(kind, def.name), dataset: { kind } },
    thumb(kind, 'w-10 h-10'),
    el('div', 'flex-1', {},
      el('div', 'text-sm', {}, def.name + (def.blurb ? ` · ${def.blurb}` : '')),
      el('div', 'text-[11px] text-text-dim font-mono inline-flex items-center gap-1', {},
        ...richText(`🪵 ${def.wood}${def.stone ? ` · 🪨 ${def.stone}` : ''}${def.buildMin ? ` · 🔨 ${def.buildMin} min` : ''}`)),
      el('div', 'text-[11px] text-error/90', { dataset: { lock: '1' } }, '')));

  /** One compact chip. Thirty-odd fences/roads/props as full rows is a wall of
   *  buttons nobody scans, so everything cosmetic goes in wrapping strips. */
  const chip = ([kind, def]: [string, typeof BUILDINGS[string]]) => el('button',
    `flex items-center gap-2 px-2.5 py-1.5 rounded border ${affordable(def) ? 'border-border hover:border-gold' : 'border-border opacity-50 cursor-not-allowed'}`,
    { title: `${def.name} · Wood ${def.wood} · Stone ${def.stone}${def.buildMin ? ` · builds in ${def.buildMin} min` : ''}${def.blurb ? ` · ${def.blurb}` : ''}`, onClick: () => arm(kind, def.name) },
    // The straight/centre piece is the honest thumbnail: corners, verges and end
    // caps are chosen by the connector once the run is on the ground.
    thumb(kind, 'w-8 h-8'),
    el('div', 'text-left', {},
      el('div', 'text-sm leading-tight', {}, def.name),
      el('div', 'text-[11px] text-text-dim font-mono inline-flex items-center gap-1', {},
        ...richText(`${def.stone ? `🪨 ${def.stone}` : `🪵 ${def.wood}`}${def.buildMin ? ` · 🔨 ${def.buildMin}m` : ''}`))));

  const section = (label: string, list: [string, typeof BUILDINGS[string]][], render: typeof chip) => {
    if (!list.length) return;
    card.append(el('div', 'text-[11px] uppercase tracking-wider text-text-dim mt-2 mb-1.5', {}, label));
    if (render === bigRow) { for (const e of list) card.append(render(e)); return; }
    const strip = el('div', 'flex flex-wrap gap-1.5');
    for (const e of list) strip.append(render(e));
    card.append(strip);
  };

  // The tent leads the sheet, above the buildings, because it is the only entry
  // here that is not built: you already own it, it costs nothing, and it is the
  // answer to "where is it safe to stand". A player looking for shelter opens this
  // panel, and it needs to be the first thing in it rather than buried under a list
  // of things they cannot yet afford.
  {
    const t = c.tent;
    const stowed = t ? !t.pitched : false;
    card.append(el('div', 'text-[11px] uppercase tracking-wider text-text-dim mt-1 mb-1.5', {}, 'Camp'));
    card.append(el('button',
      'w-full flex items-center gap-3 p-2 mb-1.5 rounded border border-gold/60 hover:border-gold text-left',
      { onClick: () => { closePlanter(c); if (t) openTentMenu(c, t); else startTentPlacement(c); } },
      el('div', 'w-10 h-10 flex items-center justify-center shrink-0', {},
        el('img', 'max-w-full max-h-full object-contain',
          { src: '/sprites/props/tent_small.png', style: 'image-rendering:pixelated' })),
      el('div', 'flex-1', {},
        el('div', 'text-sm text-gold', {}, stowed ? 'Tent · packed' : 'Tent · pitched'),
        el('div', 'text-[11px] text-text-dim', {},
          stowed ? 'Pitch it somewhere — mobs will not attack you near it'
            : `Safe ground for ${t?.safeRadius ?? TENT_SAFE_RADIUS_TILES} tiles around it · tap to move it`))));
  }

  // Four buckets, in the order they matter: what you need, then what you fence,
  // then what you pave, then what you sprinkle around for looks.
  section('Structures', entries.filter(([k, d]) => !d.fence && !isDecorKind(k)), bigRow);
  section('Fences — keep tapping, they join up', entries.filter(([, d]) => d.fence), chip);
  section('Roads & bridges — walk right over them', entries.filter(([k]) => isGroundKind(k)), chip);
  section('Decorations', entries.filter(([k]) => isDecorKind(k) && !isGroundKind(k)), chip);
  const sheet = dockedSheet(c, '🏗️ Build',
    'Pick a structure — a ghost of it follows your cursor. Green = allowed, red = blocked (it says why).', card);
  sheet.dataset.panel = 'build';       // lets the Build button toggle THIS panel shut
  playSfx('ui_open');
  openOverlay(c, sheet);
  paintBuildLocks(c);
}

/**
 * Grey out the rows the settlement tier has not unlocked yet and say what unlocks
 * them. Re-run when the population lands, so the sheet corrects itself in place.
 *
 * Rows carry `data-kind` for exactly this: rebuilding the whole sheet on an async
 * answer would fight a player who is already scrolling it.
 */
function paintBuildLocks(c: FarmCtx) {
  const root = c.overlay;
  if (!root || root.dataset.panel !== 'build') return;
  for (const node of Array.from(root.querySelectorAll('[data-kind]')) as HTMLElement[]) {
    const need = BUILDINGS[node.dataset.kind ?? '']?.needPop;
    const locked = !!need && c.population !== null && c.population < need;
    node.classList.toggle('opacity-40', locked);
    const note = node.querySelector('[data-lock]') as HTMLElement | null;
    // textContent, so an emoji here would reach the screen as a system glyph; the
    // pack has no padlock, so the state is stated in words.
    if (note) note.textContent = locked ? `Locked — reach ${need} villagers to build this` : '';
  }
}

/** Lockout countdown as Hh Mm. */
function fmtLock(ms: number): string {
  const m = Math.ceil(ms / 60_000);
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
}

/**
 * The line under the rest circles: when the next one comes back.
 *
 * OWNER (2026-08-17), after the charges were made to regenerate: "show the
 * countdown timer under the rest circles". It is the other half of that fix — a
 * counter reading "1/5" with no timer is indistinguishable from one that is never
 * coming back, which is exactly how the missing regen went unnoticed for weeks.
 *
 * Returns null when there is nothing to say: a full set has no next charge, and an
 * older server sends no timer at all. Pure so the wording is testable; the seconds
 * are only shown under a minute, because a "59:31" that ticks every frame draws the
 * eye away from the game for the fifty-nine minutes it does not matter.
 */
export function fmtNextCharge(refills: number, maxRefills: number, nextMs: number | undefined): string | null {
  if (nextMs === undefined || refills >= maxRefills || nextMs <= 0) return null;
  const secs = Math.ceil(nextMs / 1000);
  if (secs < 60) return `next in ${secs}s`;
  const m = Math.floor(secs / 60);
  if (m < 60) return `next in ${m}m`;
  return `next in ${Math.floor(m / 60)}h ${m % 60}m`;
}

/**
 * Rest: +30 energy for one charge. When the charges are gone the server starts a
 * 4-hour lockout; offer the Shroom extension rather than just blocking, since that
 * is the whole point of the limiter (brief §3).
 */
async function doRest(c: FarmCtx) {
  try {
    await dispatchAction('field.rest', async (id) => {
      const st = await fieldApi.rest(id);
      setEnergyStatus(c, st);
      setFarm(c, await fieldApi.get());  // energy changed; refresh the bar's fill too
      paintChrome(c);                     // refreshHud only updates the top-bar coins/shrooms
      // The response, not c.energyStatus: reading it back off the context made the
      // toast depend on nothing having replaced it in between, and TypeScript was
      // right to call it possibly-null. The charges REGENERATE now, so this also
      // reports the number the server actually settled on rather than a local guess.
      const back = fmtNextCharge(st.refills, st.maxRefills, st.nextRefillMs);
      toast(`Rested · ${st.refills}/${st.maxRefills} left${back ? ` · ${back}` : ''}`);
    });
  } catch (e) {
    if (isDropped(e)) return;
    // A lapsed session surfaced here as the bare word "Unauthorized" — the raw
    // server message, printed as if resting had been refused. Route it through the
    // shared handler so the player is told what happened and sent to sign in.
    if (handleSessionExpiry(e)) return;
    // Out of charges → offer the extension instead of a dead end.
    if (c.energyStatus && (c.energyStatus.refills <= 0 || c.energyStatus.lockedOut)) return openEnergyModal(c);
    toast(e instanceof ApiError ? e.message : 'Could not rest', 'error');
  }
}

/**
 * Sleep at your own home: a full bar, +20 HP, the rest charges back and the
 * lockout gone — free, and at any hour once a Hut is standing.
 *
 * The server owns every one of those decisions (field.service `sleep`): this only
 * asks, and reports whatever it answers. Which is why the daytime refusal a
 * home-less player gets is a plain toast, not a special case here.
 */
export async function doSleep(c: FarmCtx) {
  try {
    await dispatchAction('field.sleep', async (id) => {
      setEnergyStatus(c, await fieldApi.sleep(id));
      setFarm(c, await fieldApi.get());   // energy + HP changed; refill the bars
      // paintChrome raises the "fast asleep" screen off the new status — sleeping
      // is four hours now, so the click STARTS a night, it does not finish one.
      // The old toast ("full energy, rested up") announced a restore that has not
      // happened yet and will not until the timer is up or bought off.
      paintChrome(c);
      toast('You turn in 💤 — the farm can wait');
    });
  } catch (e) {
    if (isDropped(e)) return;
    toast(e instanceof ApiError ? e.message : 'Could not sleep', 'error');
  }
}

/** The "out of energy" modal: buy an extension, or wait it out. */
export function openEnergyModal(c: FarmCtx) {
  const en = c.energyStatus;
  const card = el('div', 'bg-surface border border-border rounded-lg p-5 w-[320px] max-w-[90vw] shadow-lift text-center sheet-card', {},
    el('h3', 'font-heading text-gold mb-2', {}, 'Worn out'),
    el('p', 'text-sm text-text-dim mb-4', {},
      // With a home standing there is no dead end left to explain: sleeping there
      // is free, instant and clears this very lockout, so say so instead of
      // showing a timer the player no longer has to wait out.
      en?.home ? 'Go home and sleep it off — a full bar, free, any hour.'
        : en?.lockedOut ? `You need to rest. Locked out for ${fmtLock(en.lockedOutMs)}. Build a Hut and you can sleep instead.`
        : 'No rest charges left today. Build a Hut to sleep them back.'),
    el('button', 'w-full px-3 py-2 mb-2 min-h-[44px] rounded bg-shroom/20 border border-shroom text-shroom hover:bg-shroom/30',
      { onClick: () => void confirmPurchase({
        title: 'Buy a rest charge?',
        item: 'Rest Charge',
        grants: '+1 rest charge (a rest restores 30 energy) and clears the worn-out lockout',
        cost: en?.extensionCostShrooms ?? 10,
        onConfirm: () => buyExtension(c),
      }) },
      el('span', 'inline-flex items-center justify-center gap-1', {}, ...richText(`Buy +1 rest · 🍄 ${en?.extensionCostShrooms ?? 10}`))),
    el('button', 'w-full px-3 py-2 rounded border border-border text-text-dim hover:text-text',
      { onClick: () => closeEnergyModal() }, 'Wait it out'));
  energyOverlay?.remove();
  // z-[60] (not z-50): the immersive floating cluster sits at z-56, so at z-50 this
  // card's own buttons were behind it. Escape is wired here rather than through
  // openOverlay because this modal is deliberately NOT c.overlay — it may sit over
  // an open panel — so it owns its own listener and tears it down on close.
  energyOverlay = modalWrap(() => closeEnergyModal(), card, 'z-[60]');
  document.body.appendChild(energyOverlay);
  energyEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); closeEnergyModal(); } };
  window.addEventListener('keydown', energyEsc, true);
}
let energyOverlay: HTMLElement | null = null;
let energyEsc: ((e: KeyboardEvent) => void) | null = null;
function closeEnergyModal() {
  energyOverlay?.remove(); energyOverlay = null;
  if (energyEsc) { window.removeEventListener('keydown', energyEsc, true); energyEsc = null; }
}

async function buyExtension(c: FarmCtx) {
  try {
    await dispatchAction('field.extension', async (id) => {
      setEnergyStatus(c, await fieldApi.buyEnergyExtension(id));
      paintChrome(c);
      toast('Bought +1 rest');
      closeEnergyModal();
      await c.game.refreshHud();
      refreshBag(c);
    });
  } catch (e) {
    if (isDropped(e)) return;
    toast(e instanceof ApiError ? e.message : 'Not enough Shrooms', 'error');
  }
}

/**
 * The purchase confirmation — nothing in the vitals shelf spends currency without
 * going through here first.
 *
 * It states the four things a player needs to not feel robbed: what he is buying,
 * what it grants, the price, and his balance before AND after. The balance is
 * pulled fresh from the server when the popup opens rather than read off the HUD,
 * so the "after" figure is arithmetic on a number the server just confirmed.
 *
 * Confirm is disabled when he cannot afford it — the popup says how short he is
 * instead of letting him tap into a server rejection.
 */
export async function confirmPurchase(opts: {
  title: string;
  item: string;
  grants: string;
  cost: number;
  /**
   * Which wallet pays. Defaults to Shrooms because that is what every caller
   * wanted when this was written — but the shop sells coin-priced boosters too,
   * and quoting a coin price against the Shroom balance would tell the player
   * they cannot afford something they can.
   */
  currency?: 'shrooms' | 'coins';
  onConfirm: () => Promise<void>;
}): Promise<void> {
  const currency = opts.currency ?? 'shrooms';
  const glyph = currency === 'shrooms' ? '🍄' : '🪙';
  const balanceLabel = currency === 'shrooms' ? 'Your Shrooms' : 'Your Coins';
  const shortLabel = currency === 'shrooms' ? 'Shrooms' : 'Coins';
  let balance: number | null = null;
  try {
    const w = await shopApi.wallet();
    balance = currency === 'shrooms' ? w.shrooms : w.coins;
  } catch { /* offline: show the price, let the server rule */ }
  const q = balance === null ? null : purchaseQuote(opts.cost, balance);

  // The value carries the SHROOM TOKEN, not a mushroom emoji.
  //
  // These rows took a plain string, and a plain string is rendered verbatim — so
  // `🍄 40` came out as the operating system's red-and-white toadstool, which is
  // not the purple token the player sees on the header chip, in the shop or on
  // the button directly below. The price of a purchase is the last place the
  // currency should look like something else. (The buttons already went through
  // `richText`, which swaps the emoji for the icon; only these rows did not.)
  const row = (label: string, value: string, tone = 'text-text') =>
    el('div', 'flex items-baseline justify-between gap-3 text-sm', {},
      el('span', 'text-text-dim', {}, label),
      el('span', `font-mono ${tone} inline-flex items-center gap-1`, {}, ...richText(value)));

  let busy = false;
  const confirmBtn = el('button',
    'flex-1 px-3 py-2 min-h-[44px] rounded bg-shroom/20 border border-shroom text-shroom hover:bg-shroom/30 disabled:opacity-40',
    {}, ...richText(`Confirm · ${glyph} ${opts.cost}`)) as HTMLButtonElement;
  const cancelBtn = el('button', 'flex-1 px-3 py-2 min-h-[44px] rounded border border-border text-text-dim hover:text-text',
    {}, 'Cancel') as HTMLButtonElement;

  const card = el('div', 'bg-surface border border-border rounded-lg p-5 w-[320px] max-w-[92vw] shadow-lift sheet-card game-font', {},
    el('h3', 'font-heading text-gold mb-1 text-center', {}, opts.title),
    el('p', 'text-xs text-text-dim mb-3 text-center', {}, opts.grants),
    el('div', 'flex flex-col gap-1 mb-3', {},
      row('Item', opts.item),
      row('Price', `${glyph} ${opts.cost}`),
      row(balanceLabel, balance === null ? '—' : `${glyph} ${balance}`),
      row('After purchase', q ? `${glyph} ${q.after}` : '—', q && !q.affordable ? 'text-error' : 'text-text')),
    q && !q.affordable
      ? el('p', 'text-xs text-error mb-3 text-center', {}, `You are ${q.short} ${shortLabel} short — top up in the shop.`)
      : el('span', 'hidden'),
    el('div', 'flex gap-2', {}, cancelBtn, confirmBtn));

  if (q && !q.affordable) confirmBtn.disabled = true;

  const overlay = modalWrap(() => close(), card, 'z-[70]'); // above the energy modal (z-60)
  const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); close(); } };
  let settle: () => void = () => {};
  function close() { overlay.remove(); window.removeEventListener('keydown', esc, true); settle(); }
  document.body.appendChild(overlay);
  window.addEventListener('keydown', esc, true);

  cancelBtn.addEventListener('click', () => close());
  confirmBtn.addEventListener('click', () => {
    // Local guard on top of the server's actionId dedup: the request is in flight
    // for a beat, and a second tap must not even leave the browser.
    if (busy) return;
    busy = true;
    confirmBtn.disabled = true;
    confirmBtn.textContent = 'Buying…';
    // A REJECTED purchase must say so. This was `void onConfirm().finally(close)`
    // with no catch: a caller whose body threw — insufficient funds, a 4xx, a
    // dropped connection — closed the dialog silently and left an unhandled
    // rejection, so the player tapped Confirm and simply nothing happened. The
    // failure is reported here rather than relying on every caller to remember,
    // because forgetting is invisible.
    void opts.onConfirm()
      .catch((e: unknown) => toast(e instanceof ApiError ? e.message : 'Purchase failed', 'error'))
      .finally(() => close());
  });

  // Resolve when the dialog is ANSWERED, not when it is shown.
  //
  // This used to return as soon as the overlay was appended, so `await
  // confirmPurchase(...)` gave a caller no serialisation at all: two fast taps on
  // a shop tile opened two stacked dialogs, each with its own in-flight guard, and
  // confirming both bought the booster twice.
  await new Promise<void>((resolve) => { settle = resolve; });
}

/**
 * Confirm selling something back for Shrooms — the mirror of confirmPurchase.
 *
 * Its own dialog rather than confirmPurchase with a flag: that one is built
 * around a PRICE and an "after purchase" balance. A sale names only the end
 * price — owner (2026-10-19): "don't show fee … just direct end price".
 */
export async function confirmSellBack(opts: {
  item: string;
  /** The end price. The fee is never shown (owner, 2026-10-19). */
  net: number;
  /** What the player loses by selling, said plainly. */
  loses: string;
  onConfirm: () => Promise<void>;
}): Promise<void> {
  let busy = false;
  const confirmBtn = el('button',
    'flex-1 px-3 py-2 min-h-[44px] rounded bg-shroom/20 border border-shroom text-shroom hover:bg-shroom/30 disabled:opacity-40',
    {}, ...richText(`Sell for 🍄 ${opts.net}`)) as HTMLButtonElement;
  const cancelBtn = el('button', 'flex-1 px-3 py-2 min-h-[44px] rounded border border-border text-text-dim hover:text-text',
    {}, 'Cancel') as HTMLButtonElement;

  const card = el('div', 'bg-surface border border-border rounded-lg p-5 w-[320px] max-w-[92vw] shadow-lift sheet-card game-font', {},
    el('h3', 'font-heading text-gold mb-1 text-center break-words', {},
      ...richText(`Sell ${opts.item} for 🍄 ${opts.net}?`)),
    el('p', 'text-xs text-text-dim mb-3 text-center', {}, opts.loses),
    el('div', 'flex gap-2', {}, cancelBtn, confirmBtn));

  const overlay = modalWrap(() => close(), card, 'z-[70]');
  const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); close(); } };
  let settle: () => void = () => {};
  function close() { overlay.remove(); window.removeEventListener('keydown', esc, true); settle(); }
  document.body.appendChild(overlay);
  window.addEventListener('keydown', esc, true);

  cancelBtn.addEventListener('click', () => close());
  confirmBtn.addEventListener('click', () => {
    if (busy) return;
    busy = true;
    confirmBtn.disabled = true;
    confirmBtn.textContent = 'Selling…';
    void opts.onConfirm()
      .catch((e: unknown) => toast(e instanceof ApiError ? e.message : 'Sale failed', 'error'))
      .finally(() => close());
  });
  await new Promise<void>((resolve) => { settle = resolve; });
}

/**
 * Wounded → buy a healing draught, with the same confirmation.
 * @param onDone re-pulls whatever bar triggered this (the HP bar polls, but the
 *               player should see the effect on the tap, not up to 2.5s later).
 */
export async function openHealPurchase(onDone?: () => void | Promise<void>): Promise<void> {
  let offer;
  try { offer = await shopApi.healOffer(); } catch { toast('Shop unreachable', 'error'); return; }
  await confirmPurchase({
    title: 'Buy a healing draught?',
    item: offer.name,
    grants: offer.blurb,
    cost: offer.cost,
    onConfirm: async () => {
      try {
        await dispatchAction('shop.heal', async (id) => {
          const h = await shopApi.buyHeal(id);
          toast(`Healed +${h.healed} · ${h.hp}/${h.maxHp} HP`);
          await onDone?.();
          await gameRefreshHud();
        });
      } catch (e) {
        if (isDropped(e)) return;
        if (handleSessionExpiry(e)) return;
        toast(e instanceof ApiError ? e.message : 'Could not buy the draught', 'error');
      }
    },
  });
}

/**
 * The HP bar is mounted by the farm view and never handed a FarmCtx, so the heal
 * reaches the top-bar wallet through the last context paintChrome saw. Null off
 * the farm (arena, dungeon), where there is no farm HUD to refresh anyway.
 */
let lastCtx: FarmCtx | null = null;
async function gameRefreshHud(): Promise<void> {
  await lastCtx?.game.refreshHud();
}

// ─── Hotbar (DOM): 4 tool slots + 4 item slots + the bag ─────────────────────
// Pack UI art (slot frames, tool icons, backpack). Clicking a tool selects it
// (corner-ticked slot); item slots preview the bag's first stacks; the bag
// slot opens the full inventory modal.
const TOOLS = [
  { id: 'knife', label: 'Knife' },
  { id: 'hoe', label: 'Hoe' },
  { id: 'axe', label: 'Axe' },
  { id: 'pickaxe', label: 'Pickaxe' },
] as const;

const pxImg = (src: string, cls = 'w-8 h-8') => el('img', `${cls} object-contain pointer-events-none`, { src, style: 'image-rendering:pixelated' });

function hotbarSlot(selected: boolean, title: string, onClick: (() => void) | null, ...children: (HTMLElement | string)[]) {
  // `flex-none`: the bar is a flex row inside a width-constrained band, and in
  // portrait the slots were shrunk to 16x48 — a 48px painted sprite squeezed into
  // a sixth of its width. They keep their size and the bar scrolls instead.
  const s = el('button', 'relative w-12 h-12 flex-none flex items-center justify-center', {
    title,
    style: `background:url(/sprites/ui/hotbar/${selected ? 'slot_sel' : 'slot'}.png) center / contain no-repeat;image-rendering:pixelated`,
  }, ...children);
  if (onClick) s.addEventListener('click', onClick);
  // Handler-less slots (the empty item previews) must not read as pressable: no
  // pointer cursor, no hover, and skipped by keyboard/AT navigation.
  else { s.disabled = true; s.classList.add('cursor-default'); }
  return s;
}

/**
 * Compact HUD: the phone/narrow-window hotbar.
 *
 * The full bar is eleven slots wide — four tools, a torch, an auto-attack toggle,
 * four item previews and the bag. On a phone held in landscape that runs the whole
 * width of the screen and lands under the thumb that is trying to drive the
 * joystick. The owner's call: on mobile show exactly SIX slots and nothing else —
 * sword · hoe · axe · pickaxe · torch · bag. Everything the item preview slots
 * showed is one tap away inside the bag anyway.
 *
 * Width-based rather than touch-based, so a narrow desktop window gets the same
 * treatment instead of overflowing — this is a web AND mobile-web game.
 */
export function isCompactHud(): boolean {
  // Width OR height: a landscape phone is 844 wide and 390 tall, so the old
  // width-only test left it with the full desktop HUD. mobile/layout.ts owns the
  // breakpoints now; this stays as the name the rest of the farm imports.
  return isPhoneLayout();
}

/**
 * Which slots the hotbar shows, in order. Pure so the mobile/desktop contract can
 * be asserted in a test rather than eyeballed on a phone.
 *
 * `sep` are the thin divider rules, `item` are bag previews (always four boxes on
 * desktop, empty or not — the owner's "4 free boxes after torch").
 */
export type HotbarSlotKind = 'knife' | 'hoe' | 'axe' | 'pickaxe' | 'torch' | 'auto' | 'sep' | 'item' | 'bag';

export function hotbarLayout(compact: boolean, swordSelected: boolean): HotbarSlotKind[] {
  const tools = TOOLS.map((t) => t.id as HotbarSlotKind);
  // Phone: the six the owner asked for, in that order. No auto-attack toggle, no
  // dividers, no bag-item previews — the torch is one of the six, not dropped.
  if (compact) return [...tools, 'torch', 'bag'];
  return [
    ...tools, 'torch',
    ...(swordSelected ? (['auto'] as HotbarSlotKind[]) : []),
    'sep', 'item', 'item', 'item', 'item', 'sep', 'bag',
  ];
}

// The bag refresher lives in context.ts (which cannot import this module), so the
// painter is registered here at load time.
setBagPainter((ctx) => paintHotbar(ctx));

/**
 * The last farm context whose hotbar was painted. Settings (which cannot reach the
 * farm — it is opened by the shell, above every view) edits the tool→icon mapping and
 * fires `tb-tool-icons-changed`; without this the hotbar kept the old icons until the
 * next repaint, which is what made the Tools panel look like it did nothing.
 */
let lastHudCtx: FarmCtx | null = null;
window.addEventListener('tb-tool-icons-changed', () => {
  // Only if this hotbar is still on screen — a stale context from a torn-down farm
  // would repaint into a detached node.
  if (lastHudCtx && lastHudCtx.hotbar.isConnected) paintHotbar(lastHudCtx);
});

/**
 * Pick (or un-pick) a hotbar tool — the single writer for `selectedTool` outside
 * input.ts's own auto-equip (`autoSelectFor`, which runs the same teardown). Every
 * other affordance that swaps the hand — the ready-crop hoe in plots.ts, the torch
 * slot below — routes through here rather than assigning the field itself; a static
 * guard in build-arm.test.ts fails the build if a new one does not.
 *
 * It goes through cancelPlacementForHand first: taking a tool out ENDS placement, and
 * skipping that left the red building ghost stuck to the cursor and every map tap
 * still routed into the build branch, so the tool you just picked did nothing.
 */
export function selectHotbarTool(c: FarmCtx, id: string): void {
  cancelPlacementForHand(c);
  c.selectedTool = c.selectedTool === id ? null : id;
  if (c.tileFrame && c.selectedTool !== 'hoe') c.tileFrame.visible = false;
  if (c.selectedTool) setTorchLit(c, false);   // one hand: a tool puts the torch out
  syncHeld(c);
}

/**
 * Raise or snuff the carried torch, hand and placement included.
 *
 * Raising it empties the hand, and it does that by toggling the held tool OFF through
 * selectHotbarTool instead of clearing `selectedTool` behind its back — so the build
 * ghost is torn down on this path too. Snuffing the torch arms nothing, but an armed
 * placement still has to go: the tap belongs to the torch slot, not to the ground.
 */
export function selectTorch(c: FarmCtx, on: boolean): void {
  if (on && c.selectedTool) selectHotbarTool(c, c.selectedTool);
  else cancelPlacementForHand(c);
  setTorchLit(c, on);
}

export function paintHotbar(c: FarmCtx) {
  lastHudCtx = c;
  clear(c.hotbar);
  const compact = isCompactHud();
  for (const t of TOOLS) {
    // Tier on the slot. Upgrading used to change nothing visible on the bar, so the
    // owner could not tell a mythril axe from the wooden one he started with. The
    // icon takes the metal's wash (same tintIcon the bag panel uses) and a two-letter
    // badge rides the corner, because a phone has no hover to reveal the title.
    const tierKey = TOOL_TIER_KEY[t.id as TieredTool];
    const tier = c.gearTiers?.[tierKey] ?? 0;
    // The wash layer inside tintIcon is `absolute inset-0` with a center/contain
    // mask, so it paints at the size of whatever box wraps it. Handing it the raw
    // 32px icon inside the 48px slot stretched that wrapper to 48px: the metal
    // silhouette rendered ~1.5x larger than and offset from the sprite, and only on
    // the metal tiers (Wood/Stone get the bare img back), so the bar was
    // inconsistent slot to slot. Wrapping in a box sized to the icon — same shape
    // as the crafting row's `w-8` cell — makes tinted and untinted slots identical
    // apart from colour.
    const icon = el('span', 'relative block w-8 h-8', { style: 'line-height:0' },
      tintIcon(pxImg(toolIconUrl(t.id as ToolId)), tier));
    const slot = hotbarSlot(c.selectedTool === t.id,
      toolSlotTitle(t.label, t.id as TieredTool, tier, {
        chopEnergy: c.woodCfg?.energyCostChop, mineEnergy: c.mineCfg?.energyCostMine,
        orePerRock: c.mineCfg?.orePerRock, gemChance: c.mineCfg?.gemChance,
      }),
      () => { selectHotbarTool(c, t.id); paintHotbar(c); },
      icon,
      // Badge sits bottom-left so it never collides with the item-count badge
      // (bottom-right) or the selection tick. Colour comes from tierTint; Wood and
      // Stone have no metal in that table and stay on the neutral dim token rather
      // than getting a colour invented for them.
      ...(tier > 0 ? [el('span',
        `absolute bottom-0 left-0.5 text-[9px] leading-none px-0.5 rounded bg-black/70 pointer-events-none${toolTierColor(tier) ? '' : ' text-text-dim'}`,
        toolTierColor(tier) ? { style: `color:${toolTierColor(tier)}` } : {}, toolTierShort(tier))] : []));
    slot.dataset.tut = `tool-${t.id}`;   // tutorial coach anchors its arrow here
    c.hotbar.append(slot);
  }
  // Torch slot: carry a lit torch in hand. It is not a tool (nothing to swing) —
  // it just burns and lights the ground around the hero, so it gets its own slot
  // rather than a ToolId. Its icon is the same pack frame a placed torch uses.
  // Shown on BOTH bars: the torch is one of the phone's six slots.
  {
    const slot = hotbarSlot(c.torchLit, c.torchLit ? 'Put the torch out' : 'Carry a lit torch',
      () => { selectTorch(c, !c.torchLit); paintHotbar(c); },
      pxImg('/sprites/buildings/torch.png'));
    slot.dataset.tut = 'tool-torch';
    c.hotbar.append(slot);
  }
  // Auto-attack toggle — only meaningful (and only shown) with the sword out. The
  // Arena stays manual; this governs field combat only.
  if (c.selectedTool === 'knife' && !compact) {
    const auto = el('button',
      `w-12 h-12 flex flex-col items-center justify-center rounded border text-[10px] leading-tight ${c.autoAttack ? 'border-gold text-gold bg-gold/10' : 'border-border text-text-dim'}`,
      { title: c.autoAttack ? 'Auto-attack ON — click to disable' : 'Auto-attack OFF — click to enable',
        onClick: () => { c.autoAttack = !c.autoAttack; toast(`Auto-attack ${c.autoAttack ? 'on' : 'off'}`); paintHotbar(c); } },
      ...richText('⚔️', 16), el('span', '', {}, c.autoAttack ? 'Auto' : 'Manual'));
    c.hotbar.append(auto);
  }
  // Item previews: dropped from the compact bar (the bag button covers them).
  if (!compact) c.hotbar.append(el('div', 'w-px h-9 bg-border mx-0.5'));
  const items: { icon: string; label: string; qty: number }[] = [];
  if (c.bagCache && !compact) {
    for (const [key, label] of BAG_RES) if (c.bagCache.resources[key] > 0) items.push({ icon: itemIconSrc(key), label, qty: c.bagCache.resources[key] });
    // Every bag id, not just produce: this used to build `produce_<id>.png` for
    // ANY item, so bones, leather and cooked meat previewed as broken images.
    for (const it of c.bagCache.items) items.push({ icon: itemIconSrc(it.itemId), label: itemLabel(c, it.itemId), qty: it.quantity });
  }
  for (let i = 0; !compact && i < 4; i++) {
    const it = items[i];
    // A FILLED preview opens the bag it is previewing; an EMPTY one is inert. They
    // used to be buttons with a null handler either way — they hovered like live tool
    // slots and did nothing, so a player learned the hotbar was half-broken.
    c.hotbar.append(hotbarSlot(false,
      it ? `${it.label} ×${it.qty} — open bag` : 'Empty slot — items you carry show here',
      it ? () => openBag(c) : null,
      ...(it ? [pxImg(it.icon, ICON_CLS),
        el('span', 'absolute bottom-0 right-0.5 text-[10px] font-mono text-white leading-none px-0.5 rounded bg-black/60', {}, it.qty > 999 ? '1k+' : String(it.qty))] : [])));
  }
  if (!compact) c.hotbar.append(el('div', 'w-px h-9 bg-border mx-0.5'));
  c.hotbar.append(hotbarSlot(false, 'Open bag', () => openBag(c), pxImg('/sprites/ui/hotbar/bag.png')));
}

// ─── Crafting (DOM modal) ─────────────────────────────────────────────────────
// Mirrors TIER_NAME in backend crafting.config. Truncated at Mythril, every
// tier-7-to-9 row rendered its tier as a bare number ("axe · 7") because the
// lookup fell off the end of the array — the metals existed and the ladder worked,
// but the panel could not name the top of it.
const TIER_NAME = ['—', 'Wood', 'Stone', 'Copper', 'Iron', 'Gold', 'Mythril', 'Emerald', 'Amber', 'Ruby'];
const ARMOR_TIER_NAME = ['None', 'Stone', 'Copper', 'Iron', 'Gold', 'Mythril', 'Emerald', 'Amber', 'Ruby'];
// Distinct real icons per station / equip slot (was ambiguous emoji that collapsed
// several slots onto the shield sprite via the emoji→icon map).
const STATION_ICON: Record<string, IconName> = { workbench: 'hammer', furnace: 'fire', forge: 'gear' };
const SLOT_ICON: Record<string, IconName> = { axe: 'axe', pickaxe: 'pickaxe', hoe: 'hoe', sword: 'sword', helmet: 'helmet', chest: 'chestplate', legs: 'legs' };

// Emoji here are TOKENS, not output: richText swaps each for its pack sprite. Coal
// has no sprite in the pack and the black-circle emoji it used to carry was dropped
// on the way to the DOM (unmapped), so a coal cost rendered as a bare number. It is
// named in words instead — an honest label beats an invisible one.
/**
 * Gathered materials, as the game's OWN art.
 *
 * These were emoji — 🪵 🪨 ⛏️ 💎 — inside a pixel-art panel where the bars beside
 * them were real sprites, so one half of every recipe was drawn and the other half
 * was your operating system's font. Worse, ⛏️ is a PICKAXE standing in for ORE:
 * the picture was of the tool, not the material. Owner: "in crafting, used
 * incorrect items, are not provided proper items."
 *
 * Every one of these already existed on disk except coal, which had no picture at
 * all and printed as the bare word — see scripts/build-coal-icon.py.
 */
const MAT_ICON: Record<string, string> = {
  wood: '/sprites/resources/wood.png',
  stone: '/sprites/resources/stone.png',
  ore: '/sprites/resources/ore.png',
  gem: '/sprites/resources/gem.png',
  coal: '/sprites/resources/coal.png',
};
const MAT_NAME: Record<string, string> = {
  wood: 'Wood', stone: 'Stone', ore: 'Ore', gem: 'Gem', coal: 'Coal',
};
/** Bars have real pack art (sliced from the Ores sheet), so they show it rather
 *  than borrowing an emoji that would read as "some ore" again. */
const BAR_ICON: Record<string, string> = {
  barCopper: '/sprites/mining/bar_copper.png', barIron: '/sprites/mining/bar_iron.png',
  barGold: '/sprites/mining/bar_gold.png', barMythril: '/sprites/mining/bar_mythril.png',
  // Tiers 7-9. Without these the three new metals fell through to the plain-text
  // branch, so the whole top of the ladder quoted its main ingredient as the raw
  // field name — "3 barEmerald" — in a panel that draws every other bar.
  barEmerald: '/sprites/mining/bar_emerald.png', barAmber: '/sprites/mining/bar_amber.png',
  barRuby: '/sprites/mining/bar_ruby.png',
};
const BAR_NAME: Record<string, string> = {
  barCopper: 'Copper bar', barIron: 'Iron bar', barGold: 'Gold bar', barMythril: 'Mythril bar',
  barEmerald: 'Emerald bar', barAmber: 'Amber bar', barRuby: 'Ruby bar',
};

/**
 * A cost, rendered. It used to be a string of emoji, which had no way to show a
 * BAR — so the metal half of every recipe would have been invisible in the panel
 * that sells it. Nodes, not text: emoji for the gathered materials, the pack's own
 * bar sprite for the smelted ones.
 */
function costNodes(cost: CraftStatus['recipes'][number]['cost']): (HTMLElement | string)[] {
  const out: (HTMLElement | string)[] = [];
  for (const m of CRAFT_MATERIALS) {
    const n = cost[m];
    if (!n) continue;
    if (BAR_ICON[m]) {
      out.push(el('span', 'inline-flex items-center gap-0.5', { title: BAR_NAME[m] },
        pxImg(BAR_ICON[m], 'w-4 h-4'), el('span', '', {}, String(n))));
    } else if (MAT_ICON[m]) {
      out.push(el('span', 'inline-flex items-center gap-0.5', { title: MAT_NAME[m] ?? m },
        pxImg(MAT_ICON[m], 'w-4 h-4'), el('span', '', {}, String(n))));
    } else {
      out.push(`${n} ${MAT_NAME[m] ?? m}`, ' ');
    }
  }
  return out;
}

/**
 * Wash a pixel icon toward a tier's metal colour.
 *
 * A CSS `drop-shadow` of the tier colour behind the sprite plus a light
 * `hue-rotate`-free tint layer would fight the pack's own shading; the cheap,
 * legible option that keeps every pixel is a coloured multiply layer over the
 * icon via `background-color` + `mask`, which is what this does — the sprite's own
 * alpha becomes the mask, so only the drawn pixels take the metal.
 */
/**
 * An armour icon in its tier's metal — but only where the art is not already in it.
 *
 * The pack has four plate metals and the ladder has eight rungs, so the top three
 * wear the plain Iron plate (armor-art.ts `armourIsPainted`) and would otherwise be
 * indistinguishable from each other and from Iron itself. `armourTintTier` maps the
 * armour scale onto the crafting scale, which is offset by one.
 */
function paintArmourIcon(img: HTMLElement, armourTier: number): HTMLElement {
  return armourIsPainted(armourTier) ? tintIcon(img, armourTintTier(armourTier)) : img;
}

function tintIcon(img: HTMLElement, tier: number): HTMLElement {
  const color = tierTint(tier);
  if (!color) return img;
  const src = (img as HTMLImageElement).src;
  // 0.85, not 0.6: at 60% the metal was a suggestion — gold and mythril pieces
  // were hard to tell apart at slot size, which is the owner's "use correct color
  // for each item". The pack's own shading still reads through at 85%.
  const wash = el('span', 'absolute inset-0 opacity-85 pointer-events-none', {
    style: `background-color:${color};-webkit-mask:url(${src}) center/contain no-repeat;mask:url(${src}) center/contain no-repeat`,
  });
  // block w-full h-full, not inline-block: the wrapper has to keep the slot's own
  // size or the full-size icon inside it collapses back to its natural pixels.
  return el('span', 'relative block w-full h-full', { style: 'line-height:0' }, img, wash);
}

function renderCrafting(c: FarmCtx, st: CraftStatus) {
  const card = el('div', 'bg-surface border border-border rounded-lg p-4 w-[460px] max-w-[92vw] max-h-[86vh] overflow-y-auto shadow-lift sheet-card', {},
    el('div', 'flex justify-between items-center mb-1', {},
      el('h3', 'font-heading text-gold inline-flex items-center gap-1.5', {}, ...richText('🔨 Crafting')),
      el('button', 'tb-close-x text-text-dim hover:text-text text-lg', { onClick: () => closePlanter(c) }, '×')),
    // Every material, bars included — a player holding six iron bars must be able
    // to SEE them here, or the ladder looks unaffordable for no visible reason.
    //
    // Both branches draw a SPRITE. `MAT_ICON` used to hold emoji tokens, which is
    // what `richText` is for; when the gathered materials became real pack art the
    // costNodes path was updated and this one was not, so the stock strip fed a
    // FILE PATH to the emoji swapper and printed it verbatim — the panel opened
    // with "/sprites/resources/wood.png 0 /sprites/resources/stone.png 6 …" across
    // the top. Nothing was missing; the icon was being spelled out.
    el('div', 'flex flex-wrap gap-3 text-sm font-mono mb-3 text-text-dim', {},
      ...CRAFT_MATERIALS.map((m) => el('span', 'inline-flex items-center gap-1',
        { title: BAR_NAME[m] ?? MAT_NAME[m] ?? m },
        ...(BAR_ICON[m] ? [pxImg(BAR_ICON[m], 'w-4 h-4')]
          : MAT_ICON[m] ? [pxImg(MAT_ICON[m], 'w-4 h-4')]
          : [`${MAT_NAME[m] ?? m} `]),
        el('span', '', {}, String(st.materials[m] ?? 0))))));

  // Stations — build them in order; each unlocks a recipe branch (padlocked until
  // its prerequisite is up). Shown first so the lock reasons below make sense.
  card.append(el('div', 'text-[11px] uppercase tracking-wide text-text-dim mt-2 mb-1', {}, 'Stations'));
  for (const s of st.stations) {
    const row = el('div', 'flex items-center gap-3 p-2 mb-1.5 rounded border border-border', {},
      el('div', 'w-8 flex items-center justify-center', {}, STATION_ICON[s.kind] ? gicon(STATION_ICON[s.kind], 22) : el('span', '', {}, '•')),
      el('div', 'flex-1', {},
        el('div', 'text-sm', {}, s.name),
        el('div', 'text-[11px] text-text-dim font-mono inline-flex items-center gap-1 flex-wrap', {},
          ...(s.built ? richText('✅ Built')
            : !s.prereqReady ? [`Locked — build a ${s.requires} first`]
            : costNodes(s.cost)))));
    // A14: stations are placed on the map (Build mode), not built from this
    // panel — so an un-built station just shows where to make it.
    if (!s.built) {
      row.append(el('span', 'text-[11px] text-text-dim italic inline-flex items-center gap-1', {}, ...richText('place in 🏗️ Build')));
    }
    card.append(row);
  }

  const section = (title: string, slots: string[], names: string[]) => {
    card.append(el('div', 'text-[11px] uppercase tracking-wide text-text-dim mt-3 mb-1', {}, title));
    for (const slot of slots) {
      const tier = st.tiers[slot as keyof typeof st.tiers] ?? 0;
      const next = st.recipes.find((r) => r.slot === slot && r.isNext);
      // Lock state (brief §7): station-locked → padlock; missing materials → grey
      // with the cost; available → bright Craft button.
      const locked = next && !next.stationReady;
      // The cost belongs to the tier you would BUY, not the one named above it.
      //
      // The row used to read "pickaxe · Mythril" over "Next: Emerald Pickaxe — 3
      // [emerald bar]", and the owner read it the only way it scans: "for mythril
      // tools it is emerald bars". Same for the armour column, where his tier-6
      // set is EMERALD and the rung above it is Amber — whose bar art is a pale
      // gold — giving "for emerald now is provided gold bars". Nothing was
      // mismatched; the layout put a tier name and someone else's price together.
      // Naming the target immediately before its own cost is what fixes it.
      const reason: (HTMLElement | string)[] = !next ? ['Max tier — nothing above this']
        : locked ? [`Locked — requires ${next.station}`]
        : [`${next.name} costs `, ...costNodes(next.cost)];
      const row = el('div', 'flex items-center gap-3 p-2 mb-1.5 rounded border border-border', {},
        // Tools/weapons show their baked pack icon (the `bow` slot has no gicon at
        // all and rendered as a bullet); armor keeps the vector slot glyph.
        // TIER COLOUR. The pack has ONE sprite per tool and none at all per metal
        // tier, so a Mythril axe and a Wood axe were the same picture. The owner's
        // instruction was explicit — "if we don't have sprites for weapons, we need
        // to use those colors" — so the icon is tinted toward the ore the tier is
        // forged from (map/ores.ts owns the palette; the armour tier scale is
        // offset by one, hence +1). Wood/Stone tint to nothing and stay as drawn.
        // ARMOUR (anvil): the piece the hero actually wears at this tier, from the
        // same armourIconSrc the bag uses. Before this, helmet/chest/legs had no
        // CRAFT_SLOT_ICON entry and fell through to the flat vector pictogram in
        // SLOT_ICON — so the forge showed the light "standard helmet" even when
        // the character was wearing the heavy plate helm. Tier 0 has no piece yet,
        // so the pictogram stays there.
        //
        // No tint for the four tiers the pack draws — their metal is in the art.
        // Above those it is the plain Iron plate and the tint IS the metal, which
        // is what stops emerald, amber and ruby from all showing as mythril here.
        el('div', 'w-8 flex items-center justify-center', {},
          armourIconSrc(slot, tier) ? paintArmourIcon(pxImg(armourIconSrc(slot, tier)!, ICON_CLS), tier)
            : CRAFT_SLOT_ICON[slot] ? tintIcon(pxImg(CRAFT_SLOT_ICON[slot], ICON_CLS), names === ARMOR_TIER_NAME ? armourTintTier(tier) : tier)
            : SLOT_ICON[slot] ? gicon(SLOT_ICON[slot], 22) : el('span', '', {}, '•')),
        el('div', 'flex-1', {},
          // "you have X" rather than a bare "slot · X": the tier on this line is
          // what is EQUIPPED, and read as a heading it looked like a label for the
          // price underneath it.
          el('div', 'text-sm capitalize', {}, tier > 0 ? `${slot} — you have ${names[tier] ?? tier}` : `${slot} — none yet`),
          el('div', `text-[11px] font-mono inline-flex items-center gap-1 flex-wrap ${locked ? 'text-error/80' : 'text-text-dim'}`, {}, ...reason)));
      if (next && !locked) {
        row.append(el('button',
          `px-2.5 py-1 rounded border text-sm ${next.affordable ? 'border-gold text-gold hover:bg-gold/10' : 'border-border text-text-dim opacity-50 cursor-not-allowed'}`,
          { onClick: next.affordable ? () => doCraft(c, next.id) : undefined }, 'Craft'));
      }
      card.append(row);
    }
  };
  section('Tools & Weapon', st.toolSlots, TIER_NAME);
  section('Armor', st.armorSlots, ARMOR_TIER_NAME);

  closePlanter(c);
  const wrap = modalWrap(() => closePlanter(c), card);
  playSfx('ui_open');
  openOverlay(c, wrap);
}

async function doCraft(c: FarmCtx, recipeId: string) {
  try {
    const r = await dispatchAction('craft', (id) => craftingApi.craft(recipeId, id), { key: recipeId });
    playSfx('craft_complete');
    toast(stripEmoji(`Crafted ${r.status.recipes.find((x) => x.id === recipeId)?.name ?? 'item'}!`));
    renderCrafting(c, r.status);     // refresh with the returned state
    await refreshGear(c);            // re-dress the hero + refresh HUD
    // Chop/mine constants are tier-dependent and were fetched ONCE at view boot,
    // so after crafting an iron pickaxe the hotbar title kept quoting the old
    // tier's energy/ore/gem numbers until a page reload. Re-fetch them here (same
    // endpoints farm.ts boots from); on failure keep the previous values rather
    // than blanking the tooltip.
    c.woodCfg = await woodcuttingApi.status().catch(() => c.woodCfg);
    c.mineCfg = await miningApi.status().catch(() => c.mineCfg);
    await c.game.refreshHud();
      refreshBag(c);
  } catch (e) {
    if (isDropped(e)) return;      // debounced double-click — no craft was attempted
    toast(e instanceof ApiError ? e.message : 'Craft failed', 'error');
  }
}

// ─── Bag: everything farmed + mined ─────────────────────────────────────────
const BAG_RES: [string, string][] = [
  ['wood', 'Wood'],
  ['stone', 'Stone'],
  ['ore', 'Ore'],
  ['gem', 'Gems'],
  ['coal', 'Coal'],
  ['mushroom', 'Mushrooms'],
  // Raw ore and bars, one pair per metal. They are Farm columns like the five
  // above, and a bag that hid them left the player with no way to see the
  // material half of the crafting ladder they are climbing. Zero-count rows are
  // skipped by the loops below, so an early farm's bag looks exactly as before.
  ['ore_copper', 'Copper Ore'], ['bar_copper', 'Copper Bar'],
  ['ore_iron', 'Iron Ore'], ['bar_iron', 'Iron Bar'],
  ['ore_gold', 'Gold Ore'], ['bar_gold', 'Gold Bar'],
  ['ore_mythril', 'Mythril Ore'], ['bar_mythril', 'Mythril Bar'],
  // Tiers 7-9. The server has always sent these (field.service builds the payload
  // from ORE_LIST), but this list was hand-written and stopped at mythril, so the
  // three metals at the top of the ladder were invisible in the bag.
  ['ore_emerald', 'Emerald Ore'], ['bar_emerald', 'Emerald Bar'],
  ['ore_amber', 'Amber Ore'], ['bar_amber', 'Amber Bar'],
  ['ore_ruby', 'Ruby Ore'], ['bar_ruby', 'Ruby Bar'],
];

/** Every bag/hotbar icon draws at this one size — the art is already normalised. */
const ICON_CLS = 'w-8 h-8';

/** Crop name when it is a crop, otherwise the id read back as words (Raw Beef). */
function itemLabel(c: FarmCtx, itemId: string): string {
  // Seeds first: `seed_carrot` read back as words is "Seed Carrot", which is both
  // backwards and easy to skim past when you are hunting for the seeds you just
  // pressed. Named off the crop, so it matches what the planting panel calls it.
  if (itemId.startsWith('seed_')) {
    const seedCrop = cropById(c, itemId.slice('seed_'.length));
    if (seedCrop) return `${seedCrop.name} Seeds`;
  }
  const crop = cropById(c, itemId.replace(/^produce_/, ''));
  if (crop) return crop.name;
  // Only UserItem rows reach here (bag `items`); the Farm-column resources are
  // labelled from BAG_RES above. Every id in that set (raw_beef, bone, acorn, …)
  // reads correctly title-cased.
  return itemId.split('_').map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w)).join(' ');
}

/**
 * The bag, on the pack's PREMADE INVENTORY BOARD (`UI/UI/UI_Premade.png`).
 *
 * It used to open as the pack's BOOK plate — two absolutely-positioned columns
 * floating on a picture of an open book, with the items simply wrapping until they
 * ran off the page, and a completely separate scrolling drawer on phones. The
 * owner asked for the premade model instead: equipped armour down the left, tools
 * along the bottom row, a 5x5 bag that PAGES, and the coin / attack / defence
 * readout on the sheet's own stats panel.
 *
 * One layout for every screen now, not two. The board is a fixed-aspect plate at
 * an integer-ish scale (ui/panel.ts owns the geometry and the scale rule) inside a
 * scrolling wrapper — so a narrow phone pans the board rather than getting a second
 * hand-maintained layout that drifts from this one.
 */
export function openBag(c: FarmCtx) {
  guard('ui.bag', async () => {
    const [bag, st, wallet] = await Promise.all([
      fieldApi.bag(),
      craftingApi.status(),
      // The coin count is a nicety on this panel, not its reason to exist: an
      // unreachable wallet shows a dash rather than failing the whole bag open.
      shopApi.wallet().catch(() => null),
    ]);
    closePlanter(c);
    renderBagBoard(c, bag, st, wallet?.coins ?? null);
  });
}

/** One stack as the board draws it. */
interface BagStack { icon: string; label: string; qty: number }

function renderBagBoard(c: FarmCtx, bag: Awaited<ReturnType<typeof fieldApi.bag>>, st: CraftStatus, coins: number | null) {
  const stacks: BagStack[] = [];
  for (const [key, label] of BAG_RES) {
    if (bag.resources[key] > 0) stacks.push({ icon: itemIconSrc(key), label, qty: bag.resources[key] });
  }
  // itemIconSrc for EVERY id (not `produce_<id>` for all of them): drops, loot
  // and cooked food live in other sprite folders and were rendering broken.
  for (const it of bag.items) stacks.push({ icon: itemIconSrc(it.itemId), label: itemLabel(c, it.itemId), qty: it.quantity });

  // The space the board may occupy: the viewport minus the page chrome it must
  // not cover, minus this panel's own header and pager rows.
  const scale = boardScale(window.innerWidth - 24, window.innerHeight - chromeBottom() - 120);
  const board = boardPlate(scale);
  const pager = el('div', 'flex items-center justify-center gap-3 mt-2');

  /** Content laid into one painted hole. `pxImg` is already 1:1 pixel art. */
  const hole = (r: { x: number; y: number; w: number; h: number }, title: string, ...kids: (HTMLElement | string)[]) =>
    el('div', 'flex items-center justify-center', { title, style: boardRect(r.x, r.y, r.w, r.h, scale) }, ...kids);
  /**
   * An item icon that FILLS its slot.
   *
   * OWNER (2026-08-14): "armor, which is placed in item location, should be full
   * size". It was `max-w-full max-h-full`, which only ever shrinks — a 16px pack
   * icon dropped into a 40px slot stayed 16px, so armour and tools sat as small
   * marks in the middle of their plates while the bag's own art around them was
   * drawn at panel scale. `w-full h-full` scales up as well as down, and
   * object-contain keeps the sprite square, so nothing is stretched.
   */
  const fitImg = (src: string) => el('img', 'w-full h-full object-contain pointer-events-none',
    { src, style: 'image-rendering:pixelated' });

  /** A number typed into one of the sheet's three recessed stat bars. */
  const stat = (bar: { x: number; y: number; w: number; h: number }, value: string, title: string) =>
    el('div', 'flex items-center game-font text-[#f7e6cb] leading-none',
      { title, style: `${boardRect(bar.x, bar.y, bar.w, bar.h, scale)};font-size:${Math.round(bar.h * scale * 0.8)}px;padding-left:${Math.round(2 * scale)}px;text-shadow:0 1px 0 rgba(0,0,0,.45)` },
      value);

  let page = 0;
  const paint = () => {
    clear(board);
    // Equipped ARMOUR, down the equipment panel's middle column. An empty slot is
    // left as the sheet painted it — the plate already draws a silhouette there.
    st.armorSlots.forEach((slot, i) => {
      const y = ARMOUR_COL.ys[i];
      if (y === undefined) return;
      const tier = st.tiers[slot] ?? 0;
      const name = `${slot[0].toUpperCase()}${slot.slice(1)} · ${ARMOR_TIER_NAME[tier] ?? tier}`;
      if (tier > 0) {
        // The REAL piece, in its own metal (build-armor-icons.py cuts these from
        // the equipment layers the hero wears), so the bag shows what is on the
        // character rather than a pictogram that never changed. No tintIcon for the
        // metals the pack draws — the metal is in the art and a tint would fight
        // its shading; above them the plate is plain Iron and the tint is the only
        // thing carrying the tier.
        const src = armourIconSrc(slot, tier);
        board.append(hole({ x: ARMOUR_COL.x, y, w: SLOT.w, h: SLOT.h }, name,
          src ? paintArmourIcon(fitImg(src), tier) : tintIcon(fitImg(armourSlotIconSrc(slot)), armourTintTier(tier))));
      } else {
        board.append(hole({ x: ARMOUR_COL.x, y, w: SLOT.w, h: SLOT.h }, `${name} — craft one`));
      }
    });

    // Tools along the bottom row, one hole per craftable tool slot.
    st.toolSlots.forEach((slot, i) => {
      if (i >= TOOL_ROW.count) return;
      const tier = st.tiers[slot] ?? 0;
      const r = toolCellRect(i);
      const name = `${slot[0].toUpperCase()}${slot.slice(1)} · ${TIER_NAME[tier] ?? tier}`;
      board.append(tier > 0
        ? hole(r, name, tintIcon(fitImg(craftSlotIconSrc(slot)), tier))
        : hole(r, `${name} — craft one`));
    });

    // Coins, ATTACK and DEFENCE. Both numbers come from the shared helpers so the
    // bag never invents a second damage or armour ladder — see ui/panel.ts.
    board.append(
      stat(STAT_BARS.coins, coins === null ? '—' : String(coins), 'Coins'),
      stat(STAT_BARS.attack, String(bagAttack(st.tiers)), 'Attack — what one swing lands with your sword'),
      stat(STAT_BARS.defence, String(bagDefence(st.tiers)), 'Defence — damage your armour soaks per hit'));

    // The 5x5 page.
    const shown = bagPageSlice(stacks, page);
    shown.forEach((s, i) => {
      board.append(hole(bagCellRect(i), `${s.label} ×${s.qty}`, fitImg(s.icon), qtyBadge(s.qty)));
    });
    if (!stacks.length) {
      board.append(el('div', 'flex items-center justify-center text-center game-font text-[#5a3c28] px-1',
        { style: `${boardRect(BAG_GRID.x, BAG_GRID.y, BAG_GRID.pitch * BAG_GRID.cols, BAG_GRID.pitch * BAG_GRID.rows, scale)};font-size:${Math.round(4 * scale)}px` },
        'Nothing yet — chop, mine, gather and harvest!'));
    }

    // Pager: only when there IS a second page. One button on a one-page bag is a
    // dead control, and a dead control on a touch screen reads as a broken panel.
    clear(pager);
    const pages = bagPageCount(stacks.length);
    if (pages > 1) {
      const arrow = (dir: -1 | 1, src: string, label: string) => {
        // OWNER: "switchers of the tabs is barely seen — change the buttons'
        // background colors". They were a hairline border on a transparent
        // ground, sitting under the bag's own wooden plate, so the only thing
        // marking a tap target was a 1px line. Filled panel-dark ground + a gold
        // rim on hover. Font deliberately untouched, per the same instruction.
        const btn = el('button',
          'w-11 h-11 flex-none flex items-center justify-center rounded border-2 border-[#7a5535] bg-[#2b1d13] '
          + 'shadow-[inset_0_1px_0_rgba(255,255,255,.12)] hover:bg-[#3b2a1b] hover:border-gold '
          + 'active:translate-y-px disabled:opacity-30 disabled:hover:bg-[#2b1d13] disabled:hover:border-[#7a5535]',
          { title: label, onClick: () => { page = clampBagPage(page + dir, stacks.length); paint(); } },
          el('img', 'w-5 h-5 object-contain pointer-events-none', { src, style: 'image-rendering:pixelated' })) as HTMLButtonElement;
        btn.disabled = dir < 0 ? page === 0 : page >= pages - 1;
        return btn;
      };
      pager.append(
        arrow(-1, PAGER_ICON.prev, 'Previous page'),
          el('span', 'game-font text-sm text-[#f7e6cb] min-w-[70px] text-center px-2 py-1 rounded bg-[#2b1d13] border border-[#7a5535]', {}, `Page ${page + 1} / ${pages}`),
        arrow(1, PAGER_ICON.next, 'Next page'));
    }
  };
  paint();

  /**
   * The way to EAT.
   *
   * The food panel was reachable from exactly one place: clicking a campfire
   * (scene.ts armCampfireClick, "A14: cooking is done AT the campfire — no footer
   * button"). But that panel is also the only eat surface in the game, and it
   * carries the OFFER TO BUILD a campfire for players who have none — so a player
   * without a fire could neither eat nor reach the thing that would let them build
   * one. Owner: "How to eat food". The answer was: you cannot, yet.
   *
   * The bag is the right second door. It is always reachable, it is where the food
   * already is, and it does not put a button back on the footer — the campfire
   * stays the place you COOK at; this is the place you eat from.
   *
   * Dynamic import because food.ts imports scene.ts, which imports this module.
   */
  const eatBtn = el('button',
    'game-font text-sm px-3 py-2 min-h-[44px] rounded border border-[#7a5535] bg-[#2b1d13] text-[#f7e6cb] hover:border-gold hover:text-gold inline-flex items-center gap-1.5',
    { title: 'Eat something, or cook at the campfire',
      onClick: () => { void import('./food').then((m) => m.openFood(c)); } },
    ...richText('🔥 Eat / Cook'));

  const card = el('div', 'flex flex-col items-center max-w-[96vw] max-h-[88vh] overflow-auto overscroll-contain p-2', {},
    el('div', 'flex items-center justify-between gap-3 w-full mb-1', {},
      el('div', 'font-heading text-gold text-sm', {}, 'Bag'),
      el('button', 'tb-close-x w-11 h-11 flex items-center justify-center rounded border border-border text-text-dim hover:border-gold hover:text-gold text-xl leading-none',
        { title: 'Close (Esc)', onClick: () => closePlanter(c) }, '×')),
    board, pager, el('div', 'mt-2', {}, eatBtn));
  const wrap = modalWrap(() => closePlanter(c), card);
  playSfx('ui_open');
  openOverlay(c, wrap);
}


export function openCrafting(c: FarmCtx) {
  guard('ui.crafting', async () => {
    const st = await craftingApi.status();
    renderCrafting(c, st);
  });
}
